import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";

const WORSHIP_IMG = "https://images.unsplash.com/photo-1570786032462-2efc3ca8fccd?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

function SeriesRail() {
  return (
    <section className="bg-white py-16 sm:py-20" data-testid="series-rail-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">Series &amp; Studies</p>
          <h2 className="mt-3 text-balance font-display text-3xl font-900 tracking-tighter text-navy sm:text-5xl" data-testid="series-coming-soon">
            COMING SOON.
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
            Our first sermon series will be announced as we get closer to launch - one on Sundays, one on Wednesdays.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-sm text-slate-500" data-testid="ministries-links">
            Coming soon - ministries with their own pages:{" "}
            <Link to="/lifeline-youth" data-testid="ministries-link-lifeline" className="font-semibold text-victory transition-colors hover:text-victory-dark">Lifeline Youth Group</Link> ·{" "}
            <Link to="/rally-point" data-testid="ministries-link-rally" className="font-semibold text-victory transition-colors hover:text-victory-dark">The Rally Point</Link> ·{" "}
            <Link to="/in-home" data-testid="ministries-link-in-home" className="font-semibold text-victory transition-colors hover:text-victory-dark">Clay Groups</Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Sermons() {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speaker, setSpeaker] = useState("");
  const [series, setSeries] = useState("");
  const [book, setBook] = useState("");

  useEffect(() => {
    api.get("/sermons").then(({ data }) => setSermons(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const speakers = useMemo(() => [...new Set(sermons.map((s) => s.speaker).filter(Boolean))], [sermons]);
  const seriesList = useMemo(() => [...new Set(sermons.map((s) => s.series).filter(Boolean))], [sermons]);
  const books = useMemo(() => [...new Set(sermons.map((s) => (s.scripture || "").split(" ")[0]).filter(Boolean))], [sermons]);

  const filtered = useMemo(() => sermons.filter((s) =>
    (!speaker || s.speaker === speaker) &&
    (!series || s.series === series) &&
    (!book || (s.scripture || "").startsWith(book))
  ), [sermons, speaker, series, book]);

  const selectCls = "rounded-full border border-navy/15 bg-white px-4 py-2.5 text-sm font-semibold text-navy focus:border-navy focus:outline-none";

  return (
    <>
      <SEO
        title="Sermons - Bible-Centered Preaching"
        description="Watch and listen to Bible-centered messages from Victory Chapel in Clay County, FL. Filter by speaker, series and Bible book."
        path="/sermons"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vcclaychurch.com/" },
            { "@type": "ListItem", position: 2, name: "Messages" },
          ],
        }}
      />
      <section className="relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <img src={WORSHIP_IMG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" loading="eager" />
        <div className="absolute inset-0 bg-navy/70" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Sermons</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["MESSAGES FOR", "REAL LIFE."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Clear, Bible-centered preaching from Victory Chapel. Watch anytime, anywhere.
            </p>
          </Reveal>
        </div>
      </section>

      <SeriesRail />

      <section className="bg-cream py-16 sm:py-24" data-testid="sermons-list-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {!loading && sermons.length > 0 && (
            <Reveal>
              <div className="flex flex-wrap items-center gap-3" data-testid="sermon-filters">
              <span className="text-sm font-semibold text-slate-500">Filter:</span>
              <select aria-label="Filter by speaker" data-testid="filter-speaker" value={speaker} onChange={(e) => setSpeaker(e.target.value)} className={selectCls}>
                <option value="">All Speakers</option>
                {speakers.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select aria-label="Filter by series" data-testid="filter-series" value={series} onChange={(e) => setSeries(e.target.value)} className={selectCls}>
                <option value="">All Series</option>
                {seriesList.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select aria-label="Filter by Bible book" data-testid="filter-book" value={book} onChange={(e) => setBook(e.target.value)} className={selectCls}>
                <option value="">All Books</option>
                {books.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            </Reveal>
          )}

          {loading ? (
            <p className="mt-16 text-center text-slate-500">Loading sermons...</p>
          ) : filtered.length === 0 ? (
            <div className="mt-16 rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="sermons-empty">
              <h2 className="font-display text-2xl font-800 text-navy">Sermons are coming soon.</h2>
              <p className="mt-3 text-slate-600">Victory Chapel launches January 2027. Our message archive will begin with Service #1.</p>
            </div>
          ) : (
            <Stagger className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s) => (
                <StaggerItem key={s.id}>
                  <Link to={`/sermons/${s.slug}`} data-testid={`sermon-card-${s.slug}`}
                        className="group block overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                    <div className="relative aspect-video overflow-hidden bg-navy">
                      {s.youtube_id ? (
                        <img src={`https://i.ytimg.com/vi/${s.youtube_id}/hqdefault.jpg`} alt={`${s.title} sermon thumbnail`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      ) : s.thumbnail ? (
                        <img src={s.thumbnail} alt={`${s.title} sermon thumbnail`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      ) : (
                        <div className="flex h-full items-center justify-center"><PlayCircle size={48} className="text-white/40" aria-hidden="true" /></div>
                      )}
                      {s.sample && <span className="absolute left-3 top-3 rounded-full bg-victory px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Sample</span>}
                    </div>
                    <div className="p-6">
                      {s.series && <p className="text-xs font-bold uppercase tracking-[0.2em] text-victory">{s.series}</p>}
                      <h2 className="mt-2 font-display text-xl font-800 tracking-tight text-navy group-hover:text-victory">{s.title}</h2>
                      <p className="mt-2 text-sm text-slate-500">
                        {s.speaker}{s.speaker && s.date ? " - " : ""}{s.date}
                      </p>
                      {s.scripture && <p className="mt-1 font-serif text-sm italic text-slate-600">{s.scripture}</p>}
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>
    </>
  );
}
